import { format, differenceInCalendarDays } from 'date-fns'
import { fr } from 'date-fns/locale'
import { apiClient } from '@/core/api/client'
import type { ActivityItemViewModel } from '../components/ActivityItem/ActivityItem.types'
import type { CommunicationType } from '../types/tracker.types'

// Export « frise du dossier » orienté CLIENT.
//
// Récit visuel par PHASES : chaque changement de statut ouvre une phase
// colorée selon sa nature (travail de l'expert, attente de documents /
// de paiement / de validation, planification, traitement bureau…), avec
// sa DURÉE (= les périodes d'attente). Les échanges de la période sont
// imbriqués dessous, avec un marquage entrant/sortant explicite. Les
// points-clés sont rédigés par l'IA. Rendu client puis impression PDF.

export interface ExportTrackerPdfParams {
  affaireId: string | number
  address?: string
  status?: string
  missionType?: string
  amount?: number
  /** Date (+ heure) du rendez-vous d'expertise sur site. */
  visitDate?: Date | null
  activities: ActivityItemViewModel[]
}

const escapeHtml = (s: unknown): string =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const fmtAmount = (n?: number): string =>
  typeof n === 'number' && Number.isFinite(n)
    ? new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(n)
    : '—'

const clean = (s: string | undefined, max = 160): string => {
  const v = (s || '').trim().replace(/\s+/g, ' ')
  return v.length > max ? `${v.slice(0, max - 1)}…` : v
}

// ── Catégorisation des statuts en « nature de phase » ──────────────
interface PhaseCategory {
  key: string
  label: string
  /** teinte de fond du bandeau */
  bg: string
  /** couleur d'accent (barre, marqueur, texte) */
  color: string
  icon: string
  /** true = période d'attente/travail (affiche « pendant X ») */
  waiting: boolean
}

// Petites icônes inline (Heroicons-like), rendues en SVG pour le PDF.
const svg = (path: string, color: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="13" height="13">${path}</svg>`

const ICONS = {
  expert: '<path d="M11 3 4 7v5c0 5 3.5 8 7 9 3.5-1 7-4 7-9V7z"/><path d="m9 12 2 2 4-4"/>',
  doc: '<path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"/>',
  euro: '<circle cx="12" cy="12" r="9"/><path d="M15 9a4 4 0 1 0 0 6"/><path d="M7 11h5M7 13h5"/>',
  check: '<circle cx="12" cy="12" r="9"/><path d="m8.5 12 2.5 2.5 5-5"/>',
  calendar: '<rect x="4" y="5" width="16" height="16" rx="2"/><path d="M4 10h16M8 3v4M16 3v4"/>',
  office: '<path d="M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16"/><path d="M15 9h4a1 1 0 0 1 1 1v11M8 8h3M8 12h3M8 16h3"/>',
  send: '<path d="m4 12 16-7-7 16-2-7z"/>',
  flag: '<path d="M5 21V4M5 4h11l-2 4 2 4H5"/>',
  pin: '<path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/>',
}

function categorizeStatus(label: string): PhaseCategory {
  const s = (label || '').toLowerCase()
  if (/expert/.test(s))
    return { key: 'expert', label: "Travail de l'expert", bg: '#fff7ed', color: '#ea580c', icon: ICONS.expert, waiting: true }
  if (/doc|infos|renseign/.test(s))
    return { key: 'docs', label: 'Attente de documents', bg: '#eff6ff', color: '#2563eb', icon: ICONS.doc, waiting: true }
  if (/paiement|paie/.test(s))
    return { key: 'pay', label: 'Attente de paiement', bg: '#faf5ff', color: '#7c3aed', icon: ICONS.euro, waiting: true }
  if (/confirmation|valid/.test(s))
    return { key: 'valid', label: 'Attente de validation', bg: '#f0fdfa', color: '#0d9488', icon: ICONS.check, waiting: true }
  if (/planifier|rv|rendez|visite/.test(s))
    return { key: 'plan', label: 'Planification', bg: '#f0f9ff', color: '#0284c7', icon: ICONS.calendar, waiting: false }
  if (/transmettre|transmis|envoyé|clôtur|termin/.test(s))
    return { key: 'done', label: 'Prêt à transmettre', bg: '#f0fdf4', color: '#16a34a', icon: ICONS.send, waiting: false }
  return { key: 'office', label: 'Traitement par le bureau', bg: '#f8fafc', color: '#475569', icon: ICONS.office, waiting: true }
}

const CLIENT_TYPES = new Set<CommunicationType>([
  'status_update',
  'email',
  'sms_outbox',
  'sms_inbox',
  'rdv',
  'voip_call',
])

interface StatusEv { date: Date; label: string }
interface CommEv {
  aiIdx: number
  date: Date
  channel: 'email' | 'sms' | 'call' | 'rdv'
  direction: 'in' | 'out' | null
  generic: string
  /** Contexte envoyé à l'IA (pour les emails : objet + extrait du corps). */
  text: string
  /** Objet de l'email, affiché en référence sous le résumé. */
  subject?: string
}

function humanDuration(from: Date, to: Date): string {
  const days = Math.max(0, differenceInCalendarDays(to, from))
  if (days <= 0) return "moins d'un jour"
  if (days === 1) return '1 jour'
  if (days < 14) return `${days} jours`
  const weeks = Math.round(days / 7)
  if (weeks < 9) return `${weeks} semaines`
  return `${Math.round(days / 30)} mois`
}

function buildComm(a: ActivityItemViewModel): Omit<CommEv, 'aiIdx'> | null {
  switch (a.type) {
    case 'email': {
      const subject = clean(a.title, 130)
      const bodyPreview = clean(a.snippet || a.fullContent, 400)
      return {
        date: a.timestamp,
        channel: 'email',
        direction: a.direction === 'inbound' ? 'in' : 'out',
        generic: a.direction === 'inbound' ? 'Courrier reçu' : 'Courrier envoyé',
        // On donne objet + extrait du corps à l'IA pour un résumé fidèle.
        text: [subject, bodyPreview].filter(Boolean).join(' — '),
        subject,
      }
    }
    case 'sms_outbox':
    case 'sms_inbox':
      return {
        date: a.timestamp,
        channel: 'sms',
        direction: a.type === 'sms_inbox' ? 'in' : 'out',
        generic: a.type === 'sms_inbox' ? 'SMS reçu' : 'SMS envoyé',
        text: clean(a.snippet),
      }
    case 'voip_call':
      return {
        date: a.timestamp,
        channel: 'call',
        direction: a.direction === 'inbound' ? 'in' : a.direction === 'outbound' ? 'out' : null,
        generic: 'Échange téléphonique',
        text: clean(a.voipSummary || a.snippet),
      }
    case 'rdv':
      // La visite est rendue comme un jalon dédié (voir renderVisit),
      // pas comme un simple échange → on l'exclut des comms.
      return null
    default:
      return null
  }
}

const CHANNEL_ICON: Record<CommEv['channel'], string> = {
  email: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  sms: '<path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z"/>',
  call: '<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z"/>',
  rdv: '<rect x="4" y="5" width="16" height="16" rx="2"/><path d="M4 10h16M8 3v4M16 3v4"/>',
}

function renderComm(c: CommEv, keypoints: Record<string, string>): string {
  const time = format(c.date, 'd MMM · HH:mm', { locale: fr })
  const ai = keypoints[String(c.aiIdx)]
  // Emails : résumé IA en une ligne en tête, objet du courrier en dessous.
  // Autres canaux : point-clé (ou libellé générique) + éventuel détail.
  const label = ai || c.generic
  const detail =
    c.channel === 'email'
      ? c.subject && c.subject !== label
        ? `Objet : ${c.subject}`
        : ''
      : c.text && c.text !== label
        ? c.text
        : ''
  // Marquage direction : sortant (bleu, →) vs entrant (coral, ←).
  const dir =
    c.direction === 'out'
      ? '<span class="dir dir-out">→ Envoyé</span>'
      : c.direction === 'in'
        ? '<span class="dir dir-in">← Reçu</span>'
        : ''
  const dirClass = c.direction === 'out' ? 'comm--out' : c.direction === 'in' ? 'comm--in' : 'comm--neutral'
  return `
    <div class="comm ${dirClass}">
      <span class="comm-ico">${svg(CHANNEL_ICON[c.channel], 'currentColor')}</span>
      <div class="comm-body">
        <div class="comm-top">${dir}<span class="comm-label">${escapeHtml(label)}</span></div>
        ${detail ? `<div class="comm-detail">${escapeHtml(detail)}</div>` : ''}
      </div>
      <span class="comm-time">${escapeHtml(time)}</span>
    </div>`
}

// Jalon « visite du bien » — marqueur fort, distinct des statuts et
// des échanges. Programmée (futur) vs réalisée (passé).
function renderVisit(date: Date, now: Date): string {
  const done = date.getTime() <= now.getTime()
  const d = format(date, 'EEEE d MMMM yyyy', { locale: fr })
  const t = format(date, 'HH:mm')
  const day = d.charAt(0).toUpperCase() + d.slice(1)
  return `
    <div class="visit">
      <span class="visit-ico">${svg(ICONS.pin, '#ffffff')}</span>
      <div class="visit-body">
        <div class="visit-title">${done ? 'Visite du bien réalisée' : 'Visite du bien programmée'}</div>
        <div class="visit-date">${escapeHtml(day)} à ${escapeHtml(t)}</div>
      </div>
      <span class="visit-tag">${done ? 'Expert sur site' : 'À venir'}</span>
    </div>`
}

function renderPhase(
  status: StatusEv,
  end: Date,
  isCurrent: boolean,
  comms: CommEv[],
  keypoints: Record<string, string>,
  now: Date,
  visitInPhase?: Date,
): string {
  const cat = categorizeStatus(status.label)
  const started = format(status.date, 'd MMMM yyyy', { locale: fr })
  const durTxt = isCurrent
    ? `en cours · depuis ${humanDuration(status.date, end)}`
    : `${cat.waiting ? 'pendant' : 'durée'} ${humanDuration(status.date, end)}`

  // Fusionne les échanges + l'éventuel jalon visite, triés par date.
  const items: { date: Date; html: string }[] = comms.map((c) => ({
    date: c.date,
    html: renderComm(c, keypoints),
  }))
  if (visitInPhase) {
    items.push({ date: visitInPhase, html: renderVisit(visitInPhase, now) })
  }
  items.sort((a, b) => a.date.getTime() - b.date.getTime())

  return `
    <section class="phase" style="--pc:${cat.color};--pbg:${cat.bg}">
      <div class="phase-node">${svg(cat.icon, cat.color)}</div>
      <div class="phase-card">
        <div class="phase-head">
          <span class="phase-stage">${escapeHtml(status.label)}</span>
          ${isCurrent ? '<span class="phase-now">Étape actuelle</span>' : ''}
        </div>
        <div class="phase-meta">
          <span class="phase-cat">${escapeHtml(cat.label)}</span>
          <span class="phase-dur">${escapeHtml(durTxt)}</span>
          <span class="phase-since">à partir du ${escapeHtml(started)}</span>
        </div>
        ${items.length ? `<div class="phase-comms">${items.map((i) => i.html).join('')}</div>` : ''}
      </div>
    </section>`
}

function buildDoc(
  params: ExportTrackerPdfParams,
  statusEvents: StatusEv[],
  commEvents: CommEv[],
  keypoints: Record<string, string>,
): string {
  const { affaireId, address, status, missionType, amount } = params
  const now = new Date()
  const nowStr = format(now, 'd MMMM yyyy', { locale: fr })
  const logo = `${window.location.origin}/images/logo.svg`
  const logoWhite = `${window.location.origin}/images/logo-white.svg`
  const currentStage = status || (statusEvents.length ? statusEvents[statusEvents.length - 1].label : '')

  // Assemble le corps : une phase par statut, avec les comms de la
  // période imbriquées. Les comms antérieures au 1er statut sont
  // rattachées à la première phase.
  const visitDate = params.visitDate || undefined
  let visitPlaced = false

  let body = ''
  if (statusEvents.length > 0) {
    body = statusEvents
      .map((st, i) => {
        const end = i + 1 < statusEvents.length ? statusEvents[i + 1].date : now
        const isCurrent = i === statusEvents.length - 1
        const lo = i === 0 ? new Date(0) : st.date
        const hi = i + 1 < statusEvents.length ? end : new Date(8640000000000000) // dernière phase = ouverte
        const comms = commEvents.filter((c) => c.date >= lo && c.date < hi)
        let visitInPhase: Date | undefined
        if (visitDate && !visitPlaced && visitDate >= lo && visitDate < hi) {
          visitInPhase = visitDate
          visitPlaced = true
        }
        return renderPhase(st, end, isCurrent, comms, keypoints, now, visitInPhase)
      })
      .join('')
  } else if (commEvents.length > 0 || visitDate) {
    // Pas de jalon de statut : simple chronologie des échanges.
    const items: { date: Date; html: string }[] = commEvents.map((c) => ({
      date: c.date,
      html: renderComm(c, keypoints),
    }))
    if (visitDate) {
      items.push({ date: visitDate, html: renderVisit(visitDate, now) })
      visitPlaced = true
    }
    items.sort((a, b) => a.date.getTime() - b.date.getTime())
    body = `<section class="phase" style="--pc:#475569;--pbg:#f8fafc">
      <div class="phase-node">${svg(ICONS.flag, '#475569')}</div>
      <div class="phase-card">
        <div class="phase-head"><span class="phase-stage">Chronologie du dossier</span></div>
        <div class="phase-comms">${items.map((i) => i.html).join('')}</div>
      </div></section>`
  } else {
    body = '<p class="empty">Aucune étape à afficher pour le moment.</p>'
  }

  return `<!doctype html>
<html lang="fr"><head><meta charset="utf-8">
<title>Suivi du dossier ${escapeHtml(affaireId)}</title>
<style>
  @page { size: A4; margin: 15mm 14mm 20mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #1f2937; font-size: 12px; margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  /* ── En-tête : bandeau héro navy (charte rapport d'expertise) ── */
  .hero { position: relative; overflow: hidden; background: #002035; border-radius: 16px; padding: 22px 26px; color: #fff; }
  .hero::after { content: ''; position: absolute; right: -60px; top: -70px; width: 220px; height: 220px; border-radius: 50%; background: rgba(239,83,67,.14); }
  .hero::before { content: ''; position: absolute; right: 30px; bottom: -90px; width: 170px; height: 170px; border-radius: 50%; background: rgba(255,255,255,.04); }
  .hero-row { position: relative; z-index: 1; display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; }
  .hero-logo { height: 26px; }
  .hero-title { font-size: 23px; font-weight: 700; margin: 16px 0 0; position: relative; z-index: 1; letter-spacing: .2px; }
  .hero-accent { width: 46px; height: 3px; background: #ef5343; border-radius: 3px; margin: 9px 0; position: relative; z-index: 1; }
  .hero-sub { font-size: 11px; color: rgba(255,255,255,.72); position: relative; z-index: 1; }
  .hero-stage { text-align: right; background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.16); border-radius: 12px; padding: 8px 14px; }
  .hero-stage .lbl { display: block; font-size: 8px; font-weight: 600; text-transform: uppercase; letter-spacing: .09em; color: rgba(255,255,255,.6); margin-bottom: 4px; }
  .hero-stage .val { display: inline-block; background: #ef5343; color: #fff; font-weight: 700; font-size: 12px; padding: 4px 12px; border-radius: 50px; }

  /* Bandeau récapitulatif sous le héro */
  .summary { display: flex; flex-wrap: wrap; gap: 10px; margin: 12px 0 22px; }
  .summary .cell { flex: 1; min-width: 130px; background: #f8fafc; border: 1px solid #eef0f3; border-radius: 10px; padding: 9px 13px; }
  .summary .k { display: block; color: #9ca3af; font-size: 9px; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 2px; }
  .summary .v { color: #002035; font-weight: 700; font-size: 13px; }

  /* Colonne de phases */
  .phase { position: relative; padding-left: 40px; margin-bottom: 16px; break-inside: avoid; }
  .phase::before { content: ''; position: absolute; left: 15px; top: 4px; bottom: -18px; width: 2px; background: #e5e7eb; }
  .phase:last-child::before { display: none; }
  .phase-node { position: absolute; left: 0; top: 0; width: 32px; height: 32px; border-radius: 50%; background: var(--pbg); border: 2px solid var(--pc); display: flex; align-items: center; justify-content: center; z-index: 1; }

  .phase-card { background: var(--pbg); border: 1px solid #eef0f3; border-left: 3px solid var(--pc); border-radius: 10px; padding: 11px 14px; }
  .phase-head { display: flex; align-items: center; gap: 10px; }
  .phase-stage { font-size: 14px; font-weight: 700; color: #002035; }
  .phase-now { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #fff; background: var(--pc); padding: 2px 8px; border-radius: 50px; }
  .phase-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; margin-top: 4px; }
  .phase-cat { font-size: 10.5px; font-weight: 700; color: var(--pc); text-transform: uppercase; letter-spacing: .03em; }
  .phase-dur { font-size: 11px; font-weight: 600; color: #374151; background: #fff; border: 1px solid #e5e7eb; padding: 1px 9px; border-radius: 50px; }
  .phase-since { font-size: 10.5px; color: #9ca3af; }

  /* Échanges imbriqués */
  .phase-comms { margin-top: 10px; display: flex; flex-direction: column; gap: 5px; }
  .comm { display: flex; align-items: flex-start; gap: 9px; background: #fff; border: 1px solid #eef0f3; border-radius: 8px; padding: 6px 10px; }
  .comm-ico { flex: 0 0 20px; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #64748b; background: #f1f5f9; }
  .comm--out .comm-ico { color: #2563eb; background: #eff6ff; }
  .comm--in .comm-ico { color: #ef5343; background: #fef2f0; }
  .comm-body { flex: 1; min-width: 0; }
  .comm-top { display: flex; align-items: baseline; gap: 7px; }
  .dir { font-size: 9px; font-weight: 700; padding: 1px 7px; border-radius: 50px; white-space: nowrap; }
  .dir-out { color: #2563eb; background: #eff6ff; }
  .dir-in { color: #ef5343; background: #fef2f0; }
  .comm-label { font-weight: 600; color: #111827; }
  .comm-detail { color: #6b7280; font-size: 11px; margin-top: 1px; }
  .comm-time { flex: 0 0 auto; color: #9ca3af; font-size: 10px; white-space: nowrap; font-variant-numeric: tabular-nums; }

  /* Jalon fort : visite du bien par l'expert */
  .visit { display: flex; align-items: center; gap: 11px; background: linear-gradient(90deg, #ef5343, #d63d2d); border-radius: 10px; padding: 9px 13px; color: #fff; box-shadow: 0 4px 12px rgba(239,83,67,.28); }
  .visit-ico { flex: 0 0 26px; width: 26px; height: 26px; border-radius: 50%; background: rgba(255,255,255,.18); display: flex; align-items: center; justify-content: center; }
  .visit-body { flex: 1; min-width: 0; }
  .visit-title { font-weight: 700; font-size: 13px; }
  .visit-date { font-size: 11px; color: rgba(255,255,255,.85); margin-top: 1px; }
  .visit-tag { flex: 0 0 auto; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; background: rgba(255,255,255,.2); padding: 3px 9px; border-radius: 50px; white-space: nowrap; }

  /* Légende */
  .legend { display: flex; gap: 16px; margin: 4px 0 20px; padding-left: 2px; }
  .legend span { display: inline-flex; align-items: center; gap: 5px; font-size: 10px; color: #6b7280; }
  .legend .sw { width: 9px; height: 9px; border-radius: 2px; }

  .empty { color: #9ca3af; padding: 20px 0; }

  /* ── Pied de page riche, répété sur chaque page ── */
  .foot { position: fixed; left: 0; right: 0; bottom: 6mm; padding: 0 2px; }
  .foot-sep { height: 1px; background: linear-gradient(90deg, #edd7d5, #ef5343, #edd7d5); opacity: .45; margin-bottom: 5px; }
  .foot-row { display: flex; justify-content: space-between; align-items: center; font-size: 8px; }
  .foot-row .l { color: #002035; font-weight: 600; }
  .foot-row .l .muted { color: #858892; font-weight: 400; }
  .foot-row .r { color: #858892; }
  .foot-row .r .url { color: #ef5343; font-weight: 500; }
</style></head>
<body>
  <div class="hero">
    <div class="hero-row">
      <img class="hero-logo" src="${logoWhite}" alt="ChatelainXpert" onerror="this.onerror=null;this.src='${logo}'">
      ${currentStage ? `<div class="hero-stage"><span class="lbl">Statut actuel</span><span class="val">${escapeHtml(currentStage)}</span></div>` : ''}
    </div>
    <div class="hero-title">Suivi de votre dossier</div>
    <div class="hero-accent"></div>
    <div class="hero-sub">Dossier n° ${escapeHtml(affaireId)} · situation au ${nowStr}</div>
  </div>

  <div class="summary">
    ${missionType ? `<div class="cell"><span class="k">Mission</span><span class="v">${escapeHtml(missionType)}</span></div>` : ''}
    ${address ? `<div class="cell"><span class="k">Bien concerné</span><span class="v">${escapeHtml(address)}</span></div>` : ''}
    ${amount != null ? `<div class="cell"><span class="k">Honoraires</span><span class="v">${fmtAmount(amount)}</span></div>` : ''}
  </div>

  <div class="legend">
    <span><span class="sw" style="background:#2563eb"></span>Courrier envoyé</span>
    <span><span class="sw" style="background:#ef5343"></span>Courrier reçu</span>
    <span><span class="sw" style="background:#ea580c"></span>Phase d'attente / de travail</span>
    <span><span class="sw" style="background:#ef5343;border-radius:50%"></span>Visite du bien</span>
  </div>

  ${body}

  <div class="foot">
    <div class="foot-sep"></div>
    <div class="foot-row">
      <span class="l">ChatelainXpert <span class="muted">· Suivi de dossier — confidentiel</span></span>
      <span class="r"><span class="url">www.chatelainxpert.be</span> · +32 (0)85 250 750</span>
    </div>
  </div>
  <script>
    window.onload = function () { window.focus(); window.print(); window.onafterprint = function () { window.close(); }; };
  </script>
</body></html>`
}

const LOADING_DOC = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Génération…</title>
<style>body{font-family:'Inter',system-ui,sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;margin:0;color:#6b7280;background:#fff}
.s{width:34px;height:34px;border:3px solid #f3f4f6;border-top-color:#ef5343;border-radius:50%;animation:r .8s linear infinite;margin-bottom:16px}
@keyframes r{to{transform:rotate(360deg)}}</style></head>
<body><div class="s"></div><div>Génération de la frise du dossier…</div></body></html>`

export async function exportTrackerPdf(params: ExportTrackerPdfParams): Promise<void> {
  const relevant = params.activities.filter((a) => CLIENT_TYPES.has(a.type))

  const statusEvents: StatusEv[] = relevant
    .filter((a) => a.type === 'status_update')
    .map((a) => ({ date: a.timestamp, label: clean(a.title) || 'Changement de statut' }))
    .sort((a, b) => a.date.getTime() - b.date.getTime())

  const commEvents: CommEv[] = relevant
    .map(buildComm)
    .filter((c): c is Omit<CommEv, 'aiIdx'> => c !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map((c, i) => ({ ...c, aiIdx: i }))

  // Fenêtre ouverte DANS le geste utilisateur (sinon bloquée).
  const w = window.open('', '_blank', 'width=920,height=1000')
  if (w) {
    w.document.open()
    w.document.write(LOADING_DOC)
    w.document.close()
  }

  // Points-clés IA pour les échanges (best-effort → repli générique).
  let keypoints: Record<string, string> = {}
  if (commEvents.length > 0) {
    try {
      const payload = {
        steps: commEvents.map((c) => ({
          date: c.date.toISOString(),
          type: c.channel,
          direction: c.direction || '',
          text: c.text || c.generic,
        })),
      }
      const resp = await apiClient.post<{ success: boolean; keypoints: Record<string, string> }>(
        `/api/tracker/chronologic/${params.affaireId}/timeline-summary`,
        payload,
      )
      if (resp?.keypoints) keypoints = resp.keypoints
    } catch {
      /* silencieux */
    }
  }

  const doc = buildDoc(params, statusEvents, commEvents, keypoints)

  if (w) {
    w.document.open()
    w.document.write(doc)
    w.document.close()
  } else {
    const blob = new Blob([doc], { type: 'text/html' })
    window.open(URL.createObjectURL(blob), '_blank')
  }
}
